import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export interface WebsiteMinistryImage {
  key: string;
  url: string | null;
  updated_at: string;
}

export const MINISTRY_IMAGE_KEYS = [
  { key: 'mens', label: "Men's ministry" },
  { key: 'womens', label: "Women's ministry" },
  { key: 'childrens', label: "Children's ministry" },
  { key: 'teen', label: 'Teen ministry' },
  { key: 'music', label: 'Music ministry' },
  { key: 'clergy', label: 'Clergy ministry' },
  { key: 'fine-arts', label: 'Fine Arts ministry' },
  { key: 'first-touch', label: 'First Touch ministry' },
  { key: 'christian-education', label: 'Christian Education ministry' },
  { key: 'media', label: 'Media ministry' },
] as const;

export function useWebsiteMinistryImages() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['websiteMinistryImages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('website_ministry_images')
        .select('key, url, updated_at');
      if (error) throw error;
      const map = new Map<string, WebsiteMinistryImage>();
      (data ?? []).forEach((row: WebsiteMinistryImage) => map.set(row.key, row));
      return map;
    },
  });

  const upsertMutation = useMutation({
    mutationFn: async ({ key, url }: { key: string; url: string | null }) => {
      const { error } = await supabase
        .from('website_ministry_images')
        .upsert(
          { key, url, updated_at: new Date().toISOString() },
          { onConflict: 'key' }
        );
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['websiteMinistryImages'] }),
  });

  return {
    imagesByKey: query.data ?? new Map<string, WebsiteMinistryImage>(),
    loading: query.isLoading,
    error: query.error,
    setImage: upsertMutation.mutateAsync,
    setImageLoading: upsertMutation.isPending,
  };
}
